import React, { useState, useEffect } from "react";
import {
  Table,
  Spin,
  message,
  Card,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tooltip,
} from "antd";
import { useParams, useLocation } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  FilterOutlined,
  ReloadOutlined
} from "@ant-design/icons";

const { Option } = Select;
const { confirm } = Modal;

const VariationDetailsPage = () => {
  const { productId } = useParams();
  const location = useLocation();
  const userRole = localStorage.getItem("userRole");

  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fabrics/colors for filters & add/bulk modals
  const [fabrics, setFabrics] = useState([]); // [{value,label,image_url,fabricColors:[{id,color_name,image_url}]}]
  const [sizes, setSizes] = useState([]);     // [{value,label}]
  const [allFabricColors, setAllFabricColors] = useState([]); // flattened, with fabricId added

  // For Add Variation modal
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [allowedFabricColors, setAllowedFabricColors] = useState([]);

  // For single update
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [updateForm] = Form.useForm();
  const [currentVariation, setCurrentVariation] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: false,
  });

  // BULK update
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [bulkForm] = Form.useForm();
  const [bulkAllowedColors, setBulkAllowedColors] = useState([]); // colors filtered by chosen fabrics (bulk form)

  // FILTERS (table)
  const [filterSizeIds, setFilterSizeIds] = useState([]);
  const [filterFabricIds, setFilterFabricIds] = useState([]);
  const [filterColorIds, setFilterColorIds] = useState([]);

  const product = location.state?.product;

  // ──────────────────────────────────────────────────────────────
  // Fetch helpers
  // ──────────────────────────────────────────────────────────────
  const fetchVariations = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/admin/product/variations/${productId}`
      );
      if (Array.isArray(data.variations)) {
        setVariations(
          data.variations.map((v) => ({
            variationId: v.variationId || v.id,
            factory_price: v.factory_price ?? 0,
            price_euro: v.price_euro,
            size: v.size || null,
            sizeId: v.sizeId || v.size_id || null,
            fabric_name: v.fabric_name || null,
            color_name: v.color_name || null,
            fabricColorId: v.fabricColorId || v.fabric_color_id || v.colorId || null,
          }))
        );
      } else {
        setVariations([]);
      }
    } catch {
      message.error("Failed to fetch variations.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Using global fabrics-with-colors endpoint for the filter pickers.
   */
  const fetchFabricsForProduct = async (_pid) => {
    try {
      const { data } = await axiosInstance.get(`/admin/fabrics-with-colors`);
      if (data?.success && Array.isArray(data.fabrics)) {
        const mapped = data.fabrics.map((f) => ({
          label: f.fabric_name,
          value: f.id,
          image_url: f.image_url,
          fabricColors: Array.isArray(f.fabricColors) ? f.fabricColors : [],
        }));
        setFabrics(mapped);

        // Flatten with fabricId
        const allColors = [];
        mapped.forEach((fabric) => {
          (fabric.fabricColors || []).forEach((color) =>
            allColors.push({
              id: color.id,
              color_name: color.color_name,
              image_url: color.image_url,
              fabricId: fabric.value,
            })
          );
        });
        setAllFabricColors(allColors);
      } else {
        setFabrics([]);
        setAllFabricColors([]);
      }
    } catch {
      message.error("Error fetching fabrics.");
      setFabrics([]);
      setAllFabricColors([]);
    }
  };

  const fetchSizesFromBackend = async () => {
    try {
      const { data } = await axiosInstance.get("/product/sizes");
      if (data.success && Array.isArray(data.sizes)) {
        setSizes(data.sizes.map((s) => ({ label: s.size, value: s.id })));
      } else {
        message.error("Failed to load sizes.");
      }
    } catch {
      message.error("Error fetching sizes.");
    }
  };

  useEffect(() => {
    Promise.all([fetchVariations(), fetchFabricsForProduct(productId), fetchSizesFromBackend()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // ──────────────────────────────────────────────────────────────
  // Add Variation
  // ──────────────────────────────────────────────────────────────
  const handleOpenAddModal = async () => {
    addForm.resetFields();
    addForm.setFieldsValue({
      productId: Number(productId),
      product_name: product?.product_name || "",
      price_euro: 0,
      factory_price: 0,
      fabricColors: [],
    });
    setAllowedFabricColors([]);
    setAddModalVisible(true);
  };

  const handleSaveAddVariation = async () => {
    try {
      const values = await addForm.validateFields();
      const fabricId = values.fabrics ? Number(values.fabrics) : null;
      const sizeId = values.sizes ? Number(values.sizes) : null;
      const colorIds = values.fabricColors || [];

      const payload = {
        productId: Number(values.productId),
        types: ["Non Ottoman"],
        ottomanBase: 0,
        cloudBase: 0,
      };

      if (fabricId) {
        payload.fabricIds = [fabricId];
        if (colorIds.length > 0) {
          payload.fabricColorIds = { [fabricId]: colorIds };
        }
      }

      if (sizeId) {
        payload.sizeIds = [sizeId];
        payload.priceMap = { [sizeId]: Number(values.price_euro) };
        if (userRole === "super admin") {
          payload.costPriceMap = { [sizeId]: Number(values.factory_price) };
        }
      } else if (fabricId) {
        payload.priceMap = { [fabricId]: Number(values.price_euro) };
        if (userRole === "super admin") {
          payload.costPriceMap = { [fabricId]: Number(values.factory_price) };
        }
      }

      const { data } = await axiosInstance.post(
        "/admin/product-variation",
        payload
      );

      if (data.success) {
        const variationsCreated = Math.max(
          colorIds.length > 0 ? colorIds.length : 1,
          sizeId ? 1 : 1
        );

        message.success(
          `Successfully created ${variationsCreated} variation(s)!`
        );
        setAddModalVisible(false);
        addForm.resetFields();
        fetchVariations();
      } else {
        message.error(data.message || "Failed to add variation.");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to add variation.");
    }
  };

  const handleAddFabricChange = (fabricId) => {
    const allowed = allFabricColors.filter((c) => c.fabricId === fabricId);
    setAllowedFabricColors(allowed);
    addForm.setFieldsValue({
      fabricColors: allowed.map((c) => c.id),
    });
  };

  // ──────────────────────────────────────────────────────────────
  // Single update
  // ──────────────────────────────────────────────────────────────
  const handleOpenUpdateModal = (variation) => {
    setCurrentVariation(variation);
    updateForm.setFieldsValue({
      price_euro: variation.price_euro,
      factory_price: variation.factory_price,
    });
    setUpdateModalVisible(true);
  };

  const handleSaveUpdateVariation = async () => {
    try {
      const vals = await updateForm.validateFields();
      const payload = {
        price_euro: vals.price_euro,
        ...(userRole === "super admin"
          ? { factory_price: vals.factory_price }
          : {}),
      };
      const { data } = await axiosInstance.put(
        `/admin/product-variation/${currentVariation.variationId}`,
        payload
      );
      if (data.success) {
        message.success("Variation updated successfully!");
        setUpdateModalVisible(false);
        fetchVariations();
      } else {
        message.error("Failed to update variation.");
      }
    } catch {
      message.error("Failed to update variation.");
    }
  };

  const handleDeleteVariationWithConfirm = (variationId) => {
    confirm({
      title: "Delete this variation?",
      content: "This action cannot be undone.",
      onOk: async () => {
        try {
          const { data } = await axiosInstance.delete(
            `/admin/product-variation/${variationId}`
          );
          if (data.success) {
            message.success("Deleted successfully!");
            fetchVariations();
          } else {
            message.error("Failed to delete.");
          }
        } catch {
          message.error("Failed to delete.");
        }
      },
    });
  };

  // ──────────────────────────────────────────────────────────────
  // Table filters
  // ──────────────────────────────────────────────────────────────
  const handleFilterSizes = (vals) => setFilterSizeIds(vals || []);
  const handleFilterFabrics = (vals) => {
    setFilterFabricIds(vals || []);
    setFilterColorIds([]);
  };
  const filterAllowedColors = filterFabricIds.length
    ? allFabricColors.filter((c) => filterFabricIds.includes(c.fabricId))
    : [];

  const handleFilterColors = (vals) => setFilterColorIds(vals || []);
  const clearFilters = () => {
    setFilterSizeIds([]);
    setFilterFabricIds([]);
    setFilterColorIds([]);
  };

  const fabricIdToName = Object.fromEntries(fabrics.map(f => [f.value, f.label]));
  const selectedFabricNames = new Set(
    filterFabricIds.map(fid => fabricIdToName[fid]).filter(Boolean)
  );

  const filteredVariations = variations.filter((v) => {
    const bySize =
      filterSizeIds.length === 0 ||
      (v.sizeId && filterSizeIds.includes(v.sizeId)) ||
      (v.size && filterSizeIds.some((id) => {
        const opt = sizes.find((s) => s.value === id);
        return opt && opt.label === v.size;
      }));

    const byFabric =
      selectedFabricNames.size === 0 ||
      (v.fabric_name && selectedFabricNames.has(v.fabric_name));

    const byColor =
      filterColorIds.length === 0 ||
      (v.fabricColorId && filterColorIds.includes(v.fabricColorId)) ||
      (v.color_name &&
        filterColorIds.some((cid) => {
          const opt = allFabricColors.find((c) => c.id === cid);
          return opt && opt.color_name === v.color_name;
        }));

    return bySize && byFabric && byColor;
  });

  // ──────────────────────────────────────────────────────────────
  // BULK
  // ──────────────────────────────────────────────────────────────
  const handleOpenBulkModal = async () => {
    bulkForm.resetFields();
    setBulkAllowedColors([]);
    bulkForm.setFieldsValue({
      sizeIds: [],
      fabricIds: [],
      fabricColorIds: [],
      price_euro: 0,
      ...(userRole === "super admin" ? { factory_price: 0 } : {}),
    });
    setBulkModalVisible(true);
  };

  const handleBulkFabricChange = (fabricIds = []) => {
    const allowed = fabricIds.length
      ? allFabricColors.filter((c) => fabricIds.includes(c.fabricId))
      : [];
    setBulkAllowedColors(allowed);
    bulkForm.setFieldsValue({ fabricColorIds: [] });
  };

  const handleSaveBulkUpdate = async () => {
    try {
      const vals = await bulkForm.validateFields();
      const sizeIds = (vals.sizeIds || []).map(Number);
      const fabricIds = (vals.fabricIds || []).map(Number);
      const fabricColorIds = (vals.fabricColorIds || []).map(Number);

      if (sizeIds.length === 0 && fabricColorIds.length === 0 && fabricIds.length === 0) {
        message.warning("Select at least sizes or fabric colors (via fabrics).");
        return;
      }

      const finalColorIds =
        fabricColorIds.length > 0
          ? fabricColorIds
          : fabricIds.length > 0
          ? allFabricColors
              .filter((c) => fabricIds.includes(c.fabricId))
              .map((c) => c.id)
          : [];

      const payload = {
        productId: Number(productId),
        ...(sizeIds.length ? { sizeIds } : {}),
        ...(finalColorIds.length ? { fabricColorIds: finalColorIds } : {}),
        price_euro: Number(vals.price_euro),
        ...(userRole === "super admin" && vals.factory_price != null
          ? { factory_price: Number(vals.factory_price) }
          : {}),
      };

      const { data } = await axiosInstance.patch(
        "/admin/product-variation/bulk-prices",
        payload
      );

      if (data?.success) {
        message.success(data.message || "Bulk price update successful.");
        setBulkModalVisible(false);
        fetchVariations();
      } else {
        message.error(data?.message || "Bulk update failed.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to apply bulk updates."
      );
    }
  };

  // ──────────────────────────────────────────────────────────────
  // Table / columns
  // ──────────────────────────────────────────────────────────────
  const handleTableChange = (p) => setPagination(p);

  const columns = [
    {
      title: "No",
      key: "srno",
      width: 60,
      render: (_, __, index) => (
        <div className="text-center font-medium text-gray-600">
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </div>
      ),
    },
    { 
      title: "Size", 
      dataIndex: "size", 
      key: "size",
      render: (size) => <div className="font-medium text-gray-900">{size || "-"}</div>
    },
    { 
      title: "Fabric", 
      dataIndex: "fabric_name", 
      key: "fabric_name",
      render: (fabric) => <div className="text-gray-700">{fabric || "-"}</div>
    },
    { 
      title: "Fabric Color", 
      dataIndex: "color_name", 
      key: "color_name",
      render: (color) => <div className="text-gray-700">{color || "-"}</div>
    },
    ...(userRole === "super admin"
      ? [
          {
            title: "Cost Price (€)",
            dataIndex: "factory_price",
            key: "factory_price",
            width: 120,
            render: (v) => <div className="text-red-600 font-semibold">€{v}</div>,
          },
        ]
      : []),
    {
      title: "Sale Price (€)",
      dataIndex: "price_euro",
      key: "price_euro",
      width: 120,
      render: (v) => <div className="text-green-600 font-semibold">€{v}</div>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, rec) => (
        <Space>
          <Tooltip title="Update Price">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => handleOpenUpdateModal(rec)}
              className="bg-blue-500 hover:bg-blue-600 border-blue-500"
              size="small"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteVariationWithConfirm(rec.variationId)}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Product Variations
                </h1>
                <div className="flex items-center gap-4 flex-wrap">
                  <p className="text-gray-600">
                    {product?.product_name || "Product"} (ID: {productId})
                  </p>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                    {filteredVariations.length} variations
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Tooltip title="Refresh Data">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchVariations}
                    className="border-gray-300 text-gray-600 hover:border-gray-400"
                  />
                </Tooltip>
                <Button 
                  onClick={handleOpenBulkModal}
                  className="bg-purple-500 hover:bg-purple-600 border-purple-500 text-white font-medium"
                >
                  Bulk Update Prices
                </Button>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleOpenAddModal}
                  className="bg-green-500 hover:bg-green-600 border-green-500 font-medium"
                >
                  Add Variation
                </Button>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FilterOutlined className="mr-2 text-blue-500" />
                  Filter Variations
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {/* Size Filter */}
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-2">
                      Sizes
                    </label>
                    <Select
                      mode="multiple"
                      placeholder="Filter by sizes"
                      value={filterSizeIds}
                      onChange={handleFilterSizes}
                      className="w-full rounded-lg"
                      allowClear
                    >
                      {sizes.map((s) => (
                        <Option key={s.value} value={s.value}>
                          {s.label}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  {/* Fabric Filter */}
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-2">
                      Fabrics
                    </label>
                    <Select
                      mode="multiple"
                      placeholder="Filter by fabrics"
                      value={filterFabricIds}
                      onChange={handleFilterFabrics}
                      className="w-full rounded-lg"
                      allowClear
                      optionLabelProp="label"
                    >
                      {fabrics.map((f) => (
                        <Option key={f.value} value={f.value} label={f.label}>
                          <div className="flex items-center">
                            {f.image_url && (
                              <img
                                src={f.image_url}
                                alt={f.label}
                                className="w-6 h-6 mr-2 rounded border"
                              />
                            )}
                            <span>{f.label}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </div>

                  {/* Color Filter */}
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-2">
                      Fabric Colors
                    </label>
                    <Select
                      mode="multiple"
                      placeholder={
                        filterAllowedColors.length
                          ? "Filter by colors"
                          : "Select fabrics first"
                      }
                      value={filterColorIds}
                      onChange={handleFilterColors}
                      className="w-full rounded-lg"
                      allowClear
                      disabled={filterFabricIds.length === 0}
                      optionLabelProp="label"
                    >
                      {filterAllowedColors.map((c) => (
                        <Option key={c.id} value={c.id} label={c.color_name}>
                          <div className="flex items-center">
                            {c.image_url && (
                              <img
                                src={c.image_url}
                                alt={c.color_name}
                                className="w-6 h-6 mr-2 rounded-full border"
                              />
                            )}
                            <span>{c.color_name}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col justify-end">
                    <label className="text-sm font-medium text-gray-700 mb-2 invisible">
                      Actions
                    </label>
                    {/* ↓↓↓ ONLY CHANGE MADE: a small, non-stretching button */}
                    <Button
                      onClick={clearFilters}
                      size="small"
                      className="border-gray-300 text-gray-600 hover:border-gray-400 h-8 px-3 self-start"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={filteredVariations}
              columns={columns}
              rowKey="variationId"
              pagination={{
                ...pagination,
                total: filteredVariations.length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} variations`,
                className: "px-6 py-4",
                pageSizeOptions: ["10", "20", "50", "100"],
                onChange: (page) => setPagination({...pagination, current: page}),
              }}
              scroll={{ x: "max-content" }}
              className="modern-variations-table"
              rowClassName={(record) => "hover:bg-gray-50 transition-colors duration-200"}
              locale={{
                emptyText: (
                  <div className="text-center py-8">
                    <div className="text-lg font-medium text-gray-500 mb-2">No variations found</div>
                    <div className="text-sm text-gray-400">
                      {filterSizeIds.length > 0 || filterFabricIds.length > 0 || filterColorIds.length > 0 
                        ? "Try adjusting your filters" 
                        : "Add your first variation using the button above"
                      }
                    </div>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* Add New Variation Modal */}
      <Modal
        title={
          <div className="text-lg font-semibold text-gray-900">
            Add New Variation
          </div>
        }
        open={addModalVisible}
        onOk={handleSaveAddVariation}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
        }}
        width={600}
        styles={{
          body: { padding: '24px 0' },
        }}
        okText="Create Variation"
        cancelText="Cancel"
        okButtonProps={{
          className: "bg-blue-500 hover:bg-blue-600 border-blue-500",
        }}
      >
        <Form form={addForm} layout="vertical" className="px-1">
          <Form.Item name="productId" hidden initialValue={Number(productId)}>
            <Input />
          </Form.Item>
          <Form.Item name="product_name" hidden>
            <Input />
          </Form.Item>

          <div className="grid grid-cols-1 gap-4">
            <Form.Item 
              label={<span className="font-medium text-gray-700">Fabric</span>} 
              name="fabrics"
            >
              <Select
                placeholder="Select fabric (optional)"
                onChange={handleAddFabricChange}
                className="w-full rounded-lg"
                optionLabelProp="label"
                notFoundContent={
                  fabrics.length === 0 ? "No fabrics available for this product" : null
                }
              >
                {fabrics.map((f) => (
                  <Option key={f.value} value={f.value} label={f.label}>
                    <div className="flex items-center">
                      {f.image_url && (
                        <img
                          src={f.image_url}
                          alt={f.label}
                          className="w-8 h-8 mr-3 rounded border"
                        />
                      )}
                      <span>{f.label}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {addForm.getFieldValue("fabrics") && (
              <Form.Item
                label={<span className="font-medium text-gray-700">Fabric Colors</span>}
                name="fabricColors"
                rules={[
                  {
                    required: !!addForm.getFieldValue("fabrics"),
                    message: "Please select at least one color",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select fabric colors"
                  className="w-full rounded-lg"
                  optionLabelProp="label"
                >
                  {allowedFabricColors.map((c) => (
                    <Option key={c.id} value={c.id} label={c.color_name}>
                      <div className="flex items-center">
                        {c.image_url && (
                          <img
                            src={c.image_url}
                            alt={c.color_name}
                            className="w-8 h-8 mr-3 rounded-full border"
                          />
                        )}
                        <span>{c.color_name}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item 
              label={<span className="font-medium text-gray-700">Size</span>} 
              name="sizes"
            >
              <Select
                placeholder="Select size (optional)"
                className="w-full rounded-lg"
                notFoundContent={sizes.length === 0 ? "No sizes available" : null}
              >
                {sizes.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {userRole === "super admin" && (
              <Form.Item
                label={<span className="font-medium text-gray-700">Cost Price (€)</span>}
                name="factory_price"
                initialValue={0}
                rules={[{ required: true, message: "Enter the cost price" }]}
              >
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  className="w-full rounded-lg"
                  size="large"
                />
              </Form.Item>
            )}
            <Form.Item
              label={<span className="font-medium text-gray-700">Sale Price (€)</span>}
              name="price_euro"
              initialValue={0}
              rules={[{ required: true, message: "Enter the sale price" }]}
            >
              <InputNumber 
                min={0} 
                step={0.01} 
                className="w-full rounded-lg"
                size="large"
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Bulk Update Prices Modal */}
      <Modal
        title={
          <div className="text-lg font-semibold text-gray-900">
            Bulk Update Prices
          </div>
        }
        open={bulkModalVisible}
        onOk={handleSaveBulkUpdate}
        onCancel={() => {
          setBulkModalVisible(false);
          bulkForm.resetFields();
        }}
        width={600}
        styles={{
          body: { padding: '24px 0' },
        }}
        okText="Apply Bulk Update"
        cancelText="Cancel"
        okButtonProps={{
          className: "bg-purple-500 hover:bg-purple-600 border-purple-500",
        }}
      >
        <Form form={bulkForm} layout="vertical" className="px-1">
          <div className="grid grid-cols-1 gap-4">
            <Form.Item 
              label={<span className="font-medium text-gray-700">Sizes</span>} 
              name="sizeIds"
            >
              <Select
                mode="multiple"
                placeholder="(Optional) Choose sizes to update"
                className="w-full rounded-lg"
              >
                {sizes.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item 
              label={<span className="font-medium text-gray-700">Fabrics</span>} 
              name="fabricIds"
            >
              <Select
                mode="multiple"
                placeholder="(Optional) Choose fabrics"
                className="w-full rounded-lg"
                onChange={handleBulkFabricChange}
                optionLabelProp="label"
              >
                {fabrics.map((f) => (
                  <Option key={f.value} value={f.value} label={f.label}>
                    <div className="flex items-center">
                      {f.image_url && (
                        <img
                          src={f.image_url}
                          alt={f.label}
                          className="w-6 h-6 mr-2 rounded border"
                        />
                      )}
                      <span>{f.label}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item 
              label={<span className="font-medium text-gray-700">Fabric Colors</span>} 
              name="fabricColorIds"
            >
              <Select
                mode="multiple"
                placeholder={
                  bulkAllowedColors.length
                    ? "Choose fabric colors"
                    : "Select fabric(s) first"
                }
                className="w-full rounded-lg"
                optionLabelProp="label"
                disabled={bulkAllowedColors.length === 0}
              >
                {bulkAllowedColors.map((c) => (
                  <Option key={c.id} value={c.id} label={c.color_name}>
                    <div className="flex items-center">
                      {c.image_url && (
                        <img
                          src={c.image_url}
                          alt={c.color_name}
                          className="w-6 h-6 mr-2 rounded-full border"
                        />
                      )}
                      <span>{c.color_name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {userRole === "super admin" && (
              <Form.Item
                label={<span className="font-medium text-gray-700">Cost Price (€)</span>}
                name="factory_price"
                tooltip="If set, applies to all matched variations"
              >
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  className="w-full rounded-lg"
                  size="large"
                />
              </Form.Item>
            )}

            <Form.Item
              label={<span className="font-medium text-gray-700">Sale Price (€)</span>}
              name="price_euro"
              rules={[{ required: true, message: "Enter a price value" }]}
              tooltip="This will be set as the new sale price on matched variations"
            >
              <InputNumber 
                min={0} 
                step={0.01} 
                className="w-full rounded-lg"
                size="large"
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Update Variation Modal */}
      <Modal
        title={
          <div className="text-lg font-semibold text-gray-900">
            Update Variation Price
          </div>
        }
        open={updateModalVisible}
        onOk={handleSaveUpdateVariation}
        onCancel={() => {
          setUpdateModalVisible(false);
          updateForm.resetFields();
          setCurrentVariation(null);
        }}
        width={400}
        styles={{
          body: { padding: '24px 0' },
        }}
        okText="Update Price"
        cancelText="Cancel"
        okButtonProps={{
          className: "bg-blue-500 hover:bg-blue-600 border-blue-500",
        }}
      >
        <Form form={updateForm} layout="vertical" className="px-1">
          <div className="grid grid-cols-1 gap-4">
            {userRole === "super admin" && (
              <Form.Item
                label={<span className="font-medium text-gray-700">Cost Price (€)</span>}
                name="factory_price"
                rules={[{ required: true, message: "Enter the cost price" }]}
              >
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  className="w-full rounded-lg"
                  size="large"
                />
              </Form.Item>
            )}
            <Form.Item
              label={<span className="font-medium text-gray-700">Sale Price (€)</span>}
              name="price_euro"
              rules={[{ required: true, message: "Enter the sale price" }]}
            >
              <InputNumber 
                min={0} 
                step={0.01} 
                className="w-full rounded-lg"
                size="large"
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <style jsx>{`
        .modern-variations-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }
        .modern-variations-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }
        .modern-variations-table .ant-select-selector {
          border-radius: 8px !important;
        }
        .modern-variations-table .ant-input,
        .modern-variations-table .ant-input-number {
          border-radius: 8px !important;
        }
      `}</style>
    </Spin>
  );
};

export default VariationDetailsPage;
